import React from 'react';
import styled, { css } from 'styled-components';
import { Size } from '../../common/size';
import { FaSolidIconName, Icon, IconName } from '../Icons/Icon';
import { Label } from '../../Typography/Text/Text';
import { useTestId } from '../../hooks/useTestId';
import { IValidator } from '../../hooks/useValidator';
import { IThemePalette } from '../../Themes/interfaces';
import { defaultBoldTextCss } from '../../common/mixins';
import { Loader, LoadingBar } from '../../Animations/LoadingBar/LoadingBar';

export const buttonColours = (palette: IThemePalette) => ({
  button: {
    text: palette.neutral.white,
    background: palette.primary.first[100],
    disabled: {
      background: palette.primary.first[20]
    },
    focus: {
      background: palette.primary.first[75]
    }
  }
});

export interface IButtonProps extends React.ButtonHTMLAttributes<object>, IBaseButtonProps {
  label?: string;
  icon?: IconName;
  fasIcon?: FaSolidIconName;
  invertColours?: boolean;
  validators?: IValidator<any>[];
  isLoading?: boolean;
}

interface IBaseButtonProps {
  centered?: boolean;
  size?: Size;
}

const defaultSizeCss = css`
  height: 3.5rem;
  width: 14rem;
`;

const sizeStyles: Record<Size, ReturnType<typeof css>> = {
  [Size.ExtraSmall]: css`
    height: 2.5rem;
    width: 8.5rem;
  `,
  [Size.Small]: css`
    height: 2.5rem;
    width: 8.5rem;
  `,
  [Size.Medium]: defaultSizeCss,
  [Size.Large]: defaultSizeCss,
  [Size.ExtraLarge]: defaultSizeCss
};

const BaseButton = styled.button.attrs<IButtonProps>(() => ({ type: `button` }))<IButtonProps>`
  ${({ centered }) =>
    centered
      ? css`
          display: inline-block;
          margin: auto;
        `
      : css`
          display: flex;
        `}
  ${({ isLoading }) =>
    isLoading &&
    css`
      & ${Label} {
        position: relative;
      }
      & ${Label} ${Loader} {
        position: absolute;
        left: -2rem;
      }
    `}
  align-items: center;
  justify-content: center;
  position: relative;
  ${defaultBoldTextCss};
  ${({ size = Size.Medium }) => sizeStyles[size]}
  border-radius: 2rem;
  text-align: center;
  cursor: pointer;
  border: ${({ invertColours, theme }) => (invertColours ? `1px solid ${theme.colours.button.background}` : 'none')};
  ${Label} {
    justify-content: space-evenly;
  }
  ${({ disabled, invertColours, theme }) =>
    disabled &&
    css`
      cursor: not-allowed;
      background-color: ${invertColours ? theme.colours.button.text : theme.colours.button.disabled.background};
      &,
      * {
        color: ${invertColours ? theme.colours.button.disabled.background : theme.colours.button.text};
      }
    `}
  ${({ disabled, invertColours, theme }) =>
    !disabled &&
    css`
      background-color: ${invertColours ? theme.colours.button.text : theme.colours.button.background};
      &,
      * {
        color: ${invertColours ? theme.colours.button.background : theme.colours.button.text};
      }
      :hover,
      :focus {
        &,
        ${Icon},${Label} {
          background-color: ${invertColours ? theme.colours.button.text : theme.colours.button.focus.background};
          color: ${invertColours ? theme.colours.button.focus.background : theme.colours.button.text};
        }
      }
    `}  
  :active {
    opacity: 0.8;
    text-decoration: none;
  }

  ${Icon} {
    padding-left: 0;
  }
`;

export const Button = ({ children, onClick, validators = [], isLoading, disabled, ...rest }: IButtonProps) => {
  const { generateTestId } = useTestId(rest, 'Button');
  const { icon, label, fasIcon } = rest;

  const baseOnClick: React.MouseEventHandler<HTMLButtonElement> | undefined = onClick
    ? async (e) => {
        let valid = true;
        if (validators.length > 0) {
          valid = (await Promise.all(validators.map((x) => x.validate()))).every((x) => x);
        }

        if (valid) {
          onClick(e);
        }
      }
    : undefined;

  return (
    <BaseButton {...rest} onClick={baseOnClick} isLoading={isLoading} disabled={disabled} {...generateTestId()}>
      {(icon || fasIcon) && <Icon name={icon} fasName={fasIcon} {...generateTestId('Icon')} />}
      {label && (
        <Label {...generateTestId('Label')}>
          {isLoading && <LoadingBar width='1.5rem' height='1.375rem' barCount={3} invertColours={!disabled} />}
          {label}
        </Label>
      )}
      {children}
    </BaseButton>
  );
};
